import { useMemo, useRef, useState } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { AiPromptModal } from '@/components/commons/ai-prompt-modal'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Container } from '@/components/ui/container'
import { Settings01Icon, Stars01Icon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/features/auth/store/store'
import { OpsVettingHero } from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useMerchCopyAi } from '@/features/ops-vetting/hooks/use-merch-copy-ai'
import { EXPERIENCE_VETTING_PROMPT } from '@/features/ops-vetting/prompts/experience-vetting-prompt'
import { useTranslation } from '@/translations/i18n'
import type {
	ExperienceShellMerchOutputSchema,
	ExperienceVettingMerchOutputSchema
} from '../form-schemas'
import { useSubmitExperienceVetting } from '../queries/use-submit-experience-vetting'
import type { RemapExperienceVetting } from '../types'
import {
	remapExperienceMerchCopyInputForm,
	remapExperienceMerchCopyOutputForm,
	remapExperienceMerchCopyOutputFormHostEdits,
	remapMerchCopyHostEditsDiffs
} from '../utils'
import { ExpShellMerchOutputForm } from './exp-shell-merch-output-form'
import { ExpVettingMerchOutputForm } from './exp-vetting-merch-output-form'
import { ExperienceVettingMerchCopyDiffs } from './experience-vetting-merch-copy-diffs'
import { ExperienceVettingMerchSubmitModal } from './experience-vetting-merch-submit-modal'

export const ExpVettingMerchForms = ({
	experience
}: {
	experience: RemapExperienceVetting
}) => {
	const user = useAuthStore((state) => state.user)
	const isReadOnlyUser = user?.role === 'read_only'
	const isMerchandisingEditor = user?.role === 'merchandising_editor'
	const { t } = useTranslation()
	const {
		aiResponse,
		isLoading: isAiLoading,
		onMerchCopyAiCall,
		resetAiResponse
	} = useMerchCopyAi<ExperienceVettingMerchOutputSchema>()
	const isApprovalRequired =
		experience.status === 'archived_pending_host_approval' ||
		experience.status === 'archived_update_required' ||
		experience.status === 'approval_required' ||
		experience.status === 'update_required'
	const isExperienceApproved =
		experience.status === 'publishing_enabled' ||
		experience.status === 'pending_publishing' ||
		experience.status === 'pending_export' ||
		experience.status === 'approved' ||
		experience.status === 'pending_upgrade_api' ||
		experience.status === 'published_and_upgraded' ||
		experience.status === 'pending_licensing'
	const isReviewHostEdits = experience.isHostModified
	const isFormReadOnly =
		isExperienceApproved || isApprovalRequired || isReadOnlyUser
	//prompt controls
	const [currentPrompt, setCurrentPrompt] = useState<string>(
		EXPERIENCE_VETTING_PROMPT
	)
	//submission refs
	const finalSubmitRef = useRef<HTMLButtonElement>(null)
	const aiSubmissionRef = useRef<HTMLButtonElement>(null)
	//submit utils
	const [isSubmitMerchModalOpen, setIsSubmitMerchModalOpen] = useState(false)
	const [requireHostApproval, setRequireHostApproval] = useState(true)

	const { mutate: submitExperienceVetting, isPending: isSubmitting } =
		useSubmitExperienceVetting(
			{ experienceId: experience.id },
			{
				onSuccess: () => {
					toast.success(t('vetting:merch.experience.submit_success'))
				}
			}
		)

	//trigger ai-improvement submission
	const handleAiSubmission: SubmitHandler<
		ExperienceShellMerchOutputSchema
	> = (data) => {
		resetAiResponse()
		onMerchCopyAiCall(data, currentPrompt)
	}
	//final submission
	const handleFinalSubmission: SubmitHandler<
		ExperienceVettingMerchOutputSchema
	> = (data) => {
		const payload = {
			...data,
			isHostApprovalNeeded: requireHostApproval,
			isWaitlisted: false
		}
		submitExperienceVetting(payload)
	}

	// // Memoize the input form default values
	const inputFormDefaultValues = useMemo(
		() => remapExperienceMerchCopyInputForm(experience),
		[experience]
	)
	const inputFormHostEditsDefaultValues = useMemo(
		() => remapExperienceMerchCopyOutputFormHostEdits(experience),
		[experience]
	)
	// Memoize the output form default values
	const outputFormDefaultValues = useMemo(
		() =>
			aiResponse
				? (aiResponse as ExperienceVettingMerchOutputSchema)
				: remapExperienceMerchCopyOutputForm(experience),
		[experience, aiResponse]
	)
	// Memoize the diffs map
	const experienceDiffsMap = useMemo(
		() => remapMerchCopyHostEditsDiffs(experience),
		[experience]
	)

	return (
		<Container
			className='grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]'
			fullWidth
		>
			<OpsVettingHero align='center' fullWidth justify='space-between'>
				<h3>{t('vetting:merch.service.title')}</h3>
				{!isFormReadOnly && (
					<div className='flex gap-2'>
						{!(isReviewHostEdits || isApprovalRequired) && (
							<>
								<AiPromptModal
									currentPrompt={currentPrompt}
									isLoading={isAiLoading}
									onPromptChange={(newPrompt) =>
										setCurrentPrompt(newPrompt)
									}
									promptType='exp_vetting'
									trigger={
										<ButtonLegacy
											disabled={isAiLoading}
											size='sm'
											type='button'
											variant='tertiary'
										>
											<Settings01Icon />
											{t(
												'vetting:merch.commons.customize_prompt'
											)}
										</ButtonLegacy>
									}
								/>
								<ButtonLegacy
									disabled={isAiLoading}
									onClick={() =>
										aiSubmissionRef.current?.click()
									}
									size='sm'
									type='button'
									variant='secondary'
								>
									{isAiLoading ? (
										<Spinner size={16} />
									) : (
										<Stars01Icon />
									)}
									{t('vetting:merch.commons.improve_with_ai')}
								</ButtonLegacy>
							</>
						)}
						{!isMerchandisingEditor && (
							<>
								<ButtonBrandLegacy
									disabled={isAiLoading}
									onClick={() =>
										setIsSubmitMerchModalOpen(true)
									}
									size='sm'
								>
									{t('vetting:merch.commons.submit')}
								</ButtonBrandLegacy>

								{isSubmitMerchModalOpen && (
									<ExperienceVettingMerchSubmitModal
										isOpen={isSubmitMerchModalOpen}
										isSubmitting={
											isAiLoading || isSubmitting
										}
										onClose={() =>
											setIsSubmitMerchModalOpen(false)
										}
										setRequireHostApprovalCb={
											setRequireHostApproval
										}
										submissionRef={finalSubmitRef}
										type={
											isReviewHostEdits
												? 'host-edits'
												: 'ai'
										}
									/>
								)}
							</>
						)}
					</div>
				)}
			</OpsVettingHero>

			<div className='mt-6 mb-10 flex w-full overflow-hidden rounded-2xl border border-neutral-200'>
				{isReviewHostEdits ? (
					<ExpVettingMerchOutputForm
						defaultValues={inputFormHostEditsDefaultValues}
						finalSubmitRef={aiSubmissionRef}
						handleFinalSubmissionCb={undefined}
						isHostEdits={isReviewHostEdits}
						isOriginalContent={true}
						isReadOnly={isFormReadOnly}
					/>
				) : (
					<ExpShellMerchOutputForm
						defaultValues={inputFormDefaultValues}
						finalSubmitRef={aiSubmissionRef}
						handleFinalSubmissionCb={handleAiSubmission}
						isHostEdits={isReviewHostEdits}
						isLoading={isSubmitting}
						isOriginalContent={true}
						isReadOnly={isFormReadOnly}
					/>
				)}

				{isExperienceApproved || isReviewHostEdits ? (
					<ExperienceVettingMerchCopyDiffs
						experienceDiffsMap={experienceDiffsMap}
					/>
				) : (
					<Separator orientation='vertical' />
				)}
				<ExpVettingMerchOutputForm
					defaultValues={outputFormDefaultValues}
					finalSubmitRef={finalSubmitRef}
					handleFinalSubmissionCb={handleFinalSubmission}
					isHostEdits={isReviewHostEdits}
					isLoading={isAiLoading || isSubmitting}
					isReadOnly={isFormReadOnly}
				/>
			</div>
		</Container>
	)
}
ExpVettingMerchForms.displayName = 'ExpVettingMerchForms'
